import { z } from 'zod'
import { ConversationKind } from '../enums'
import { QUEUE_NAMES, type QueueName } from '../jobs'
import { DM_DEADLINE_WINDOW_MS } from '../priority'
import { PageConnection } from './channels'
import { Id, Instant } from './primitives'

export const SLA_AT_RISK_WINDOW_MS = DM_DEADLINE_WINDOW_MS

export const SLA_STATES = ['at_risk', 'breached'] as const
export const SlaState = z.enum(SLA_STATES)
export type SlaState = z.infer<typeof SlaState>

export const SlaItem = z.object({
  conversationId: Id,
  authorName: z.string(),
  kind: ConversationKind,
  replyDeadlineAt: Instant,
  priorityScore: z.number().nullable(),
  state: SlaState,
  breachedAt: Instant.nullable(),
  pageConnection: PageConnection,
})
export type SlaItem = z.infer<typeof SlaItem>

export const SlaOverview = z.object({
  atRisk: z.number().int().nonnegative(),
  breached: z.number().int().nonnegative(),
})
export type SlaOverview = z.infer<typeof SlaOverview>

export const SLA_DEFAULT_PAGE_LIMIT = 20
export const SLA_MAX_PAGE_LIMIT = 100
export const SLA_MAX_PAGE_NUMBER = 1000

export const SlaListQuery = z.strictObject({
  state: SlaState,
  page: z.coerce.number().int().min(1).max(SLA_MAX_PAGE_NUMBER).default(1),
  limit: z.coerce.number().int().min(1).max(SLA_MAX_PAGE_LIMIT).default(SLA_DEFAULT_PAGE_LIMIT),
})
export type SlaListQuery = z.infer<typeof SlaListQuery>

export const REPLAY_REFUSAL_REASONS = ['scheduled', 'spends_budget', 'unknown_queue'] as const
export const ReplayRefusalReason = z.enum(REPLAY_REFUSAL_REASONS)
export type ReplayRefusalReason = z.infer<typeof ReplayRefusalReason>

export const JOB_REPLAY_POLICY = {
  'classify-conversation': null,
  'refetch-meta-object': null,
  'fetch-post': null,
  'check-channel-health': null,
  'retire-corpus-for-source': null,
  'notify-inbox': null,
  'generate-suggestion': 'spends_budget',
  'ingest-kb-source': 'spends_budget',
  'sweep-channel-health': 'scheduled',
  'sweep-inbound-outbox': 'scheduled',
  'sweep-stranded-replies': 'scheduled',
  'sweep-auto-suggestion-outbox': 'scheduled',
  'sla-sweep': 'scheduled',
  'redact-expired': 'scheduled',
  'prune-auth-state': 'scheduled',
} as const satisfies Record<QueueName, ReplayRefusalReason | null>

export const REPLAY_REFUSAL_MESSAGES: Record<ReplayRefusalReason, string> = {
  scheduled: '這是排程工作，下一次排程會自動重跑，不需要手動重送',
  spends_budget: '重送會再次消耗本月 AI 用量，請回到原本的頁面重新產生',
  unknown_queue: '這個工作類型沒有被列為可重送，為了避免重複對外發佈，系統一律拒絕',
}

const isQueueName = (queue: string): queue is QueueName =>
  (QUEUE_NAMES as readonly string[]).includes(queue)

export const replayRefusalOf = (queue: string): ReplayRefusalReason | null =>
  isQueueName(queue) ? JOB_REPLAY_POLICY[queue] : 'unknown_queue'

export const isReplayableQueue = (queue: string): queue is QueueName => replayRefusalOf(queue) === null

export const REPLAY_OUTCOMES = ['pending', 'succeeded', 'failed', 'unknown'] as const
export const ReplayOutcome = z.enum(REPLAY_OUTCOMES)
export type ReplayOutcome = z.infer<typeof ReplayOutcome>

export const DeadLetterJob = z.object({
  id: Id,
  queue: z.string().min(1),
  failedAt: Instant,
  retryCount: z.number().int().nonnegative(),
  failureCode: z.string().min(1).nullable(),
  replayable: z.boolean(),
  refusalReason: ReplayRefusalReason.nullable(),
  replayedAt: Instant.nullable(),
  replayOutcome: ReplayOutcome.nullable(),
  replayOf: Id.nullable(),
  conversationId: Id.nullable(),
})
export type DeadLetterJob = z.infer<typeof DeadLetterJob>

export const DEAD_LETTER_DEFAULT_PAGE_LIMIT = 20
export const DEAD_LETTER_MAX_PAGE_LIMIT = 100
export const DEAD_LETTER_MAX_PAGE_NUMBER = 1000

export const DeadLetterListQuery = z.strictObject({
  page: z.coerce.number().int().min(1).max(DEAD_LETTER_MAX_PAGE_NUMBER).default(1),
  limit: z.coerce.number().int().min(1).max(DEAD_LETTER_MAX_PAGE_LIMIT).default(DEAD_LETTER_DEFAULT_PAGE_LIMIT),
})
export type DeadLetterListQuery = z.infer<typeof DeadLetterListQuery>

export const ReplayResult = z.object({ id: Id, queue: z.string().min(1), replayedAt: Instant })
export type ReplayResult = z.infer<typeof ReplayResult>

export const REPLAY_ALREADY_DONE_MESSAGE = '這個工作已經重送過了，不會再送一次'
export const DEAD_LETTER_NOT_FOUND_MESSAGE = '找不到這個失敗工作，它可能已經過期被清除'
